/**
 * Pure transforms over 3Dmol's 8-number camera view vector.
 *
 * 3DMOL-SPECIFIC, despite importing nothing from 3Dmol -- hence the name, matching
 * Visualizer3Dmol.js. This is not general camera maths that happens to be used with 3Dmol; every
 * contract here is 3Dmol's:
 *
 *   - the 8-slot view layout, and VIEW_LENGTH = 8
 *   - quaternion component order x, y, z, w -- w LAST, which many libraries do not do
 *   - the zoom convention d' = d / factor, so factor > 1 zooms IN
 *   - the rz > cameraZ "avoid getting stuck" guard, copied from GLViewer.adjustZoomToLimits
 *   - the scene composition W(v) = R(q)(v + mp) + rp, which is 3Dmol's graph, not a convention
 *
 * Swapping the renderer means rewriting this module, not re-pointing it. Do not reach for it as a
 * generic vector utility, and do not move it into math.js.
 *
 * The no-import rule is a TESTABILITY constraint, not a claim of independence: it is what lets the
 * suite run without a WebGL context while still pinning every formula against the real bundle.
 * Extracted as a standalone module for that reason -- every function is pure and takes and returns
 * plain arrays -- which leaves Visualizer3Dmol with only getView()/setView() glue.
 *
 * The view vector is 3Dmol's own GLViewer.getView() format (GLViewer.ts:1667):
 *
 *     [ mp.x, mp.y, mp.z, rz, q.x, q.y, q.z, q.w ]
 *
 * mp = modelGroup.position, rz = rotationGroup.position.z (zoom), q = rotationGroup.quaternion
 * in x,y,z,w order -- w LAST.
 *
 * Scene composition, verified against GLViewer.ts:173-179 (modelGroup is a *child* of
 * rotationGroup) and Object3D.updateMatrix (matrixWorld = parent.matrixWorld * matrix):
 *
 *     W(v) = R(q) * (v + mp) + rp,   where rp = (0, 0, rz)
 *
 * W(v) is independent of q only when v + mp === 0. Therefore:
 *
 *     the point a rotation holds fixed is always -mp.
 *
 * That single fact is why programmatic rotation was removed from this codebase in April 2026 (see
 * the note at Visualizer3Dmol.js:166-171). 3Dmol's viewer.rotate() writes the quaternion and never
 * touches mp, so it can only ever pivot about whatever happens to sit at the centre of the view.
 * Rotating about the molecule means moving mp in the same step -- which is what this module does.
 *
 * ---------------------------------------------------------------------------------------------
 * WHAT IS ACTUALLY 3DMOL-SPECIFIC IN HERE, AND WHAT IS NOT
 *
 * Counting symbols, roughly half of what is below has no 3Dmol content -- which invites the
 * question of why the file is named for 3Dmol at all. The answer is that the halves are not
 * peers: the PUBLIC surface is 6/7 3Dmol, and almost everything general is a private helper.
 * Nothing needs moving today; this is a map for whoever next wants shared vector or quaternion
 * helpers and is deciding where they live.
 *
 * EXPORTED, TIED TO 3DMOL -- take or return the 8-slot view vector, or encode a 3Dmol
 * convention. These are what the module is FOR, and they belong here whatever else happens:
 *
 *   VIEW_LENGTH             the length of getView()'s output
 *   assertValidView         validates that 8-slot shape
 *   rotateViewAboutPoint    view in, view out
 *   panView                 view in, view out
 *   zoomView                view in/out, AND 3Dmol's d' = d / factor semantics and the
 *                           rz > cameraZ guard lifted from GLViewer.adjustZoomToLimits
 *   worldPosition           implements 3Dmol's scene-graph composition, W(v) = R(q)(v + mp) + rp
 *
 * EXPORTED, GENERAL -- the one piece of the public API that is not about 3Dmol:
 *
 *   resolveAxis             named axis or vector -> unit vector, rejecting a degenerate one
 *
 * PRIVATE, GENERAL -- no 3Dmol content, but also not reusable from outside without editing this
 * file, so their presence says little about what the module is:
 *
 *   quaternionAboutAxis     axis-angle -> quaternion
 *   multiplyQuaternions     Hamilton product
 *   normalizeQuaternion     component-order agnostic; sums all four
 *   isFiniteNumber          generic guards
 *   assertFiniteTriple
 *   NAMED_AXES              generic constants
 *   MIN_AXIS_MAG_SQ
 *
 * Note the direction of travel: if the general helpers are ever extracted, what remains is
 * entirely 3Dmol, so the name gets more accurate rather than less.
 *
 * The x,y,z,w component order the quaternion helpers use is NOT a 3Dmol quirk -- it is the
 * graphics-standard layout (three.js, glTF). Only the surrounding view vector is 3Dmol's.
 *
 * Three things to know before moving any of it:
 *
 *   1. The quaternion trio is the ONLY quaternion maths in the codebase -- math.js has none.
 *      Moving them means creating a quaternion module and settling a component-order convention
 *      for everything that follows. Worth doing deliberately rather than as a drive-by.
 *
 *   2. resolveAxis CANNOT simply delegate to math.js's Normalize3, and that is why it exists.
 *      Normalize3 does `if (magSq <= 0.000001) magSq = 1`, so it returns a near-zero vector
 *      UNCHANGED rather than failing: Normalize3([0,0,0]) is [0,0,0], a non-unit "unit vector",
 *      and Normalize3([1e-4,0,0]) comes back unnormalized. Either then builds the non-unit
 *      quaternion (0,0,0,cos(theta/2)) and silently applies the wrong rotation. resolveAxis
 *      throws instead. Normalize3 has ~22 call sites across 6 files that depend on the lenient
 *      behaviour, so this is a hazard to know about, not something to change from here.
 *
 *   3. assertFiniteTriple duplicates isUsablePosition in util/atom_distance_utils.js. The
 *      predicates are identical -- array, length 3, every component a finite number -- and only
 *      the failure mode differs (throw vs return false). If a shared vector-guard ever lands,
 *      those two should collapse into one predicate plus an assert wrapper.
 * ---------------------------------------------------------------------------------------------
 */
import {
    Add3_3, Sub3_3, Mul3_1, Dot3, DegreesToRadians,
    MakeRotationMatrixAboutAxis, ApplyRotation,
} from 'BMapsSrc/math';

/** Number of elements in a 3Dmol view vector as emitted by getView(). */
export const VIEW_LENGTH = 8;

/**
 * Named axes, as unit vectors in the *model's own* frame.
 * Model frame (not screen frame) is the contract: rotate(90, 'y') means the same orientation
 * change every time, whatever the view is currently doing. That determinism is the point of a
 * programmatic API -- it is deliberately NOT mouse-drag behaviour, which is screen-relative.
 */
const NAMED_AXES = {
    x: [1, 0, 0],
    y: [0, 1, 0],
    z: [0, 0, 1],
};

/** Squared length below which an axis is treated as degenerate rather than normalized. */
const MIN_AXIS_MAG_SQ = 1e-12;

function isFiniteNumber(n) {
    return typeof n === 'number' && Number.isFinite(n);
}

function assertFiniteTriple(vec, label) {
    if (!Array.isArray(vec) || vec.length !== 3 || !vec.every(isFiniteNumber)) {
        throw new TypeError(`${label} must be an array of 3 finite numbers, got: ${JSON.stringify(vec)}`);
    }
    return vec;
}

/**
 * Validate a view vector.
 * 3Dmol's own setView() guard is broken -- `!(arg instanceof Array || arg.length !== 8)` reduces to
 * "is not an Array", so length is never actually checked and a malformed view is written straight
 * into the scene graph. We check properly here instead of relying on it.
 * @param {number[]} view
 * @returns {number[]} the same view, validated
 */
export function assertValidView(view, label = 'view') {
    if (!Array.isArray(view) || view.length !== VIEW_LENGTH || !view.every(isFiniteNumber)) {
        throw new TypeError(
            `${label} must be an array of ${VIEW_LENGTH} finite numbers, got: ${JSON.stringify(view)}`
        );
    }
    return view;
}

/**
 * Resolve an axis argument to a unit vector in the model frame.
 *
 * Rejects a degenerate axis rather than normalizing it. math.js Normalize3 substitutes magSq = 1
 * when the true magnitude is near zero, so Normalize3([0,0,0]) returns [0,0,0] -- which would then
 * build the non-unit quaternion (0,0,0,cos(theta/2)) and silently apply the wrong rotation. A bad
 * axis is a caller error and should say so.
 *
 * @param {'x'|'y'|'z'|[number, number, number]} axis
 * @returns {[number, number, number]} unit vector
 */
export function resolveAxis(axis) {
    if (typeof axis === 'string') {
        const key = axis.toLowerCase();
        // hasOwn, not a truthiness check on the lookup: NAMED_AXES is an object literal, so
        // 'constructor' and '__proto__' resolve to inherited values that are truthy. Those
        // skipped the error below and then failed on the spread instead, reporting a
        // non-iterable rather than an unknown axis.
        const named = Object.hasOwn(NAMED_AXES, key) ? NAMED_AXES[key] : undefined;
        if (!named) {
            throw new TypeError(
                `Unknown axis '${axis}'. Expected one of x, y, z, or a [x,y,z] vector.`
            );
        }
        // Copy. NAMED_AXES holds module-level singletons, and this module's contract is that
        // nothing it returns aliases its own state. A caller that normalised or negated the
        // result in place would otherwise corrupt that axis for every later rotate-view.
        return [...named];
    }

    assertFiniteTriple(axis, 'axis');
    const magSq = Dot3(axis, axis);
    if (magSq < MIN_AXIS_MAG_SQ) {
        throw new TypeError(`axis has no direction (near-zero length): ${JSON.stringify(axis)}`);
    }
    return Mul3_1(axis, 1 / Math.sqrt(magSq));
}

/**
 * Unit quaternion for a rotation of `radians` about a unit axis, in 3Dmol's x,y,z,w order.
 * Matches GLViewer.ts:1640-1650 exactly.
 */
function quaternionAboutAxis(unitAxis, radians) {
    const half = radians / 2;
    const s = Math.sin(half);
    return [unitAxis[0] * s, unitAxis[1] * s, unitAxis[2] * s, Math.cos(half)];
}

/**
 * Hamilton product a*b, both in x,y,z,w order.
 * Component-for-component identical to 3Dmol's Quaternion.multiplyQuaternions
 * (WebGL/math/Quaternion.ts:94-109), so composing here and composing in 3Dmol agree bitwise.
 */
function multiplyQuaternions(a, b) {
    const [ax, ay, az, aw] = a;
    const [bx, by, bz, bw] = b;
    return [
        ax * bw + aw * bx + ay * bz - az * by,
        ay * bw + aw * by + az * bx - ax * bz,
        az * bw + aw * bz + ax * by - ay * bx,
        aw * bw - ax * bx - ay * by - az * bz,
    ];
}

/**
 * Renormalize a quaternion.
 * The product of two unit quaternions is unit in exact arithmetic, but composing many rotations in
 * floating point lets the norm drift. 3Dmol does not renormalize its accumulated quaternion; we do,
 * because a programmatic API invites long chains of calls. The correction is at float-noise scale
 * (~1e-16 per step) so it does not change agreement with 3Dmol for any single rotation.
 */
function normalizeQuaternion(q) {
    const magSq = q[0] * q[0] + q[1] * q[1] + q[2] * q[2] + q[3] * q[3];
    if (!Number.isFinite(magSq) || magSq <= 0) {
        throw new RangeError(`Quaternion has no magnitude: ${JSON.stringify(q)}`);
    }
    const inv = 1 / Math.sqrt(magSq);
    return [q[0] * inv, q[1] * inv, q[2] * inv, q[3] * inv];
}

/**
 * Rotate a view by `degrees` about `axis`, holding the model-space point `pivot` fixed on screen.
 *
 * The transform:
 *     q'  = q * delta                      right-multiply => model frame
 *     mp' = R(delta)^-1 * (P + mp) - P     the pivot correction
 *     rz' = rz                             zoom untouched, so the slab is untouched too
 *
 * R(delta)^-1 is built directly from axis-angle as a rotation of -theta, so no quaternion is ever
 * converted to a matrix. Sanity property: when P === -mp (the pivot is already the fixed point),
 * mp' === mp exactly.
 *
 * @param {number[]} view 8-element view vector from GLViewer.getView()
 * @param {number} degrees rotation angle in degrees, positive = right-handed about `axis`
 * @param {'x'|'y'|'z'|[number,number,number]} [axis='y'] axis in the model's own frame
 * @param {[number,number,number]} [pivot] model-space point to hold fixed.
 *        Defaults to the view's current pivot, which makes the call orientation-only.
 * @returns {number[]} a new 8-element view vector; the input is never mutated
 */
export function rotateViewAboutPoint(view, degrees, axis = 'y', pivot = undefined) {
    assertValidView(view);
    if (!isFiniteNumber(degrees)) {
        throw new TypeError(`degrees must be a finite number, got: ${JSON.stringify(degrees)}`);
    }
    const unitAxis = resolveAxis(axis);

    const mp = [view[0], view[1], view[2]];
    const rz = view[3];
    const q = [view[4], view[5], view[6], view[7]];

    const pivotPoint = pivot === undefined
        ? Mul3_1(mp, -1)
        : assertFiniteTriple(pivot, 'pivot');

    const theta = DegreesToRadians(degrees);
    const delta = quaternionAboutAxis(unitAxis, theta);

    // Model frame: the delta composes on the RIGHT, matching GLViewer.ts:1651.
    const qNew = normalizeQuaternion(multiplyQuaternions(q, delta));

    // Move the model so `pivotPoint` lands back where it was: mp' = R(delta)^-1 (P + mp) - P.
    const inverseRotation = MakeRotationMatrixAboutAxis(unitAxis, -theta);
    const mpNew = Sub3_3(ApplyRotation(inverseRotation, Add3_3(pivotPoint, mp)), pivotPoint);

    const result = [mpNew[0], mpNew[1], mpNew[2], rz, qNew[0], qNew[1], qNew[2], qNew[3]];

    // Nothing non-finite may ever reach setView(). A part-NaN view is silently accepted by 3Dmol
    // and renders as a blank canvas -- the exact April "the protein disappears" symptom.
    return assertValidView(result, 'rotated view');
}

/**
 * Zoom a view by `factor`, leaving orientation and model position untouched.
 *
 * 3Dmol expresses zoom as a perceived distance d = cameraZ - rz between model and camera
 * (GLViewer.getPerceivedDistance, :4683), and its own zoom() divides that distance (:1967-1969):
 *
 *     d' = d / factor,   so   rz' = cameraZ - (cameraZ - rz) / factor
 *
 * So factor > 1 moves the camera CLOSER -- it zooms IN -- and 0 < factor < 1 zooms out. That is
 * 3Dmol's convention, kept here so viewer.zoom(2) and zoomView(view, 2, cameraZ) agree exactly.
 *
 * cameraZ is a parameter rather than the literal 150 for two reasons: GLViewer.CAMERA_Z is writable
 * at runtime (setCameraParameters, :908-909), and injecting it keeps this module free of any import
 * from 3Dmol, which is what makes it testable without a WebGL context.
 *
 * No zoom-out limit is imposed. 3Dmol clamps to config.lowerZoomLimit/upperZoomLimit
 * (adjustZoomToLimits, :503-518) but BMaps sets NEITHER -- Visualizer3Dmol.js:52-57 passes only
 * defaultcolors and cartoonQuality -- so clamping here would invent a limit the app does not have,
 * and the programmatic path would then disagree with the scroll wheel.
 *
 * The rz' > cameraZ guard below is copied from 3Dmol and is UNREACHABLE for a legal input: if d > 0
 * and factor > 0 then d' > 0, hence rz' < cameraZ. It fires only for a view that is already
 * degenerate -- the model at or behind the camera, d <= 0 -- and then it does what 3Dmol does
 * rather than emitting a view 3Dmol itself would have refused.
 *
 * @param {number[]} view 8-element view vector from GLViewer.getView()
 * @param {number} factor zoom factor; > 1 zooms in, 0 < factor < 1 zooms out
 * @param {number} cameraZ the viewer's CAMERA_Z. Derive it from the live viewer rather than
 *        hard-coding 150 -- see Visualizer3Dmol.ZoomView.
 * @returns {number[]} a new 8-element view vector; the input is never mutated
 */
export function zoomView(view, factor, cameraZ) {
    assertValidView(view);
    if (!isFiniteNumber(factor) || factor <= 0) {
        throw new TypeError(
            `factor must be a finite number greater than 0, got: ${JSON.stringify(factor)}`
        );
    }
    if (!isFiniteNumber(cameraZ)) {
        throw new TypeError(`cameraZ must be a finite number, got: ${JSON.stringify(cameraZ)}`);
    }

    const distance = cameraZ - view[3];
    let rz = cameraZ - distance / factor;
    // GLViewer.adjustZoomToLimits:515-517, "avoid getting stuck". Dead code for any legal input --
    // see the note above -- kept as defence against an input view that is already degenerate.
    if (rz > cameraZ) rz = cameraZ * 0.999;

    // Everything but rz is carried through unchanged: zoom is a change of camera distance and
    // nothing else. Note the quaternion is deliberately NOT renormalized -- an unnecessary
    // renormalization would perturb an orientation the caller did not ask to change.
    const result = [view[0], view[1], view[2], rz, view[4], view[5], view[6], view[7]];

    // Nothing non-finite may ever reach setView(). A tiny factor overflows d/factor to Infinity.
    return assertValidView(result, 'zoomed view');
}

/**
 * Pan a view by a model-space displacement, leaving orientation and zoom untouched.
 *
 * The transform:
 *     mp' = mp + delta                 translate the model, in the MODEL's own frame
 *     rz' = rz                         zoom untouched, so the slab is untouched too
 *     q'  = q                          orientation untouched, bit for bit
 *
 * Model frame, in Angstroms -- deliberately NOT mouse-drag behaviour, for the same reason
 * NAMED_AXES is model-frame. 3Dmol's own pan (translateScene -> screenOffsetToModel,
 * GLViewer.ts:753-765) divides the drag by the canvas WIDTH/HEIGHT then rotates it into model
 * space, so the same request moves the structure a different distance on a different-sized window,
 * and in a different direction at every orientation. A programmatic API has to be reproducible, so
 * this takes a vector in the model's own axes and writes it straight into modelGroup.position.
 *
 * A 3-vector, not a screen-shaped (dx, dy) pair: view[0..2] IS a model-frame 3-vector, and a
 * 2-number delta would have to be interpreted against a screen plane that changes with every
 * rotation.
 *
 * Note the documented side effect on rotation. The point a view rotates about is -mp, so panning
 * moves the DEFAULT pivot with it. Callers that want the molecule held fixed
 * pass an explicit pivot to rotateViewAboutPoint -- which is what DisplayCmds.rotateViewU already
 * does, so the shipped rotate path is unaffected by panning.
 *
 * @param {number[]} view 8-element view vector from GLViewer.getView()
 * @param {[number,number,number]} delta displacement in Angstroms along the model's own axes.
 *        The structure moves by +delta, so the camera appears to move by -delta.
 * @returns {number[]} a new 8-element view vector; the input is never mutated
 */
export function panView(view, delta) {
    assertValidView(view);
    assertFiniteTriple(delta, 'delta');

    const mp = Add3_3([view[0], view[1], view[2]], delta);
    const result = [mp[0], mp[1], mp[2], view[3], view[4], view[5], view[6], view[7]];

    // Same reason as rotateViewAboutPoint: a part-Infinity view is silently accepted by setView and
    // renders as a blank canvas.
    return assertValidView(result, 'panned view');
}

/**
 * Where a model-space point appears in world space under a given view: W(v) = R(q)(v + mp) + rp.
 * Exists so tests can assert the pivot really is held fixed, rather than asserting the formula
 * against itself. Not used by production code.
 * @param {number[]} view
 * @param {[number,number,number]} point model-space point
 * @returns {[number,number,number]} world-space position
 */
export function worldPosition(view, point) {
    assertValidView(view);
    assertFiniteTriple(point, 'point');

    const mp = [view[0], view[1], view[2]];
    const q = normalizeQuaternion([view[4], view[5], view[6], view[7]]);

    // Rotate (point + mp) by q via the conjugation v' = q v q^-1, expanded to avoid needing a
    // quaternion->matrix conversion here (the test suite pins this against 3Dmol's own Matrix4).
    const v = Add3_3(point, mp);
    const [qx, qy, qz, qw] = q;
    const ix = qw * v[0] + qy * v[2] - qz * v[1];
    const iy = qw * v[1] + qz * v[0] - qx * v[2];
    const iz = qw * v[2] + qx * v[1] - qy * v[0];
    const iw = -qx * v[0] - qy * v[1] - qz * v[2];

    // ...then translate by rp = (0, 0, rz).
    return [
        ix * qw + iw * -qx + iy * -qz - iz * -qy,
        iy * qw + iw * -qy + iz * -qx - ix * -qz,
        iz * qw + iw * -qz + ix * -qy - iy * -qx + view[3],
    ];
}
